
import { useCallback, useEffect, useState } from "react";
import type { ModuleId } from "@/lib/modules";
import { isAuthConfigured, supabase } from "@/lib/supabase";
import type { Database } from "@/types/db";
import type {
  RealtimePostgresInsertPayload,
  RealtimePostgresUpdatePayload,
  RealtimePostgresDeletePayload,
} from "@supabase/realtime-js";

/* ─────────────────────  Types  ───────────────────── */

// Must stay in sync with `public.notification_kind` (migration 012).
export type NotificationKind =
  | "chat"
  | "status"
  | "mood"
  | "list"
  | "match"
  | "tag"
  | "system"
  | "tree";

export type Notification = {
  id: string;
  kind: NotificationKind;
  /** Optional actor (for the avatar + name in the panel). The
   *  `public.notifications` table doesn't have a direct `who`
   *  column; the backend may populate the `payload` jsonb with
   *  actor info. Until then, this is left undefined. */
  who?: { name: string; relation: string; initials: string; color: string };
  /** Short title shown in the row. */
  title: string;
  /** One-line body / preview. */
  body: string;
  /** Human-readable relative time. */
  time: string;
  /** True until the user opens the panel or marks the item read. */
  unread: boolean;
  /** Module to deep-link to when the row is tapped. */
  target: ModuleId;
};

/* ─────────────────────  Row mapping  ───────────────────── */

type NotificationRow = Database["public"]["Tables"]["notifications"]["Row"];

// The DB's `deep_link_module` check constraint (migration 012)
// includes two values that the frontend's ModuleId doesn't expose
// (`famtag`, `famlov`) — the migration also doesn't know about
// `fammembers`. We map known values through and fall back to
// `today` for anything the shell can't render.
function targetFromDeepLink(
  deepLink: string | null,
  kind: NotificationKind,
): ModuleId {
  if (deepLink) {
    // Legacy `fammood` deep-link redirects to FamStatus.
    if (deepLink === "fammood") return "famstatus";
    switch (deepLink) {
      case "today":
      case "famchat":
      case "fammatch":
      case "famtree":
      case "famstatus":
      case "famlist":
      case "fammembers":
        return deepLink;
    }
  }
  // Fall back by kind so an unannotated notification still
  // navigates somewhere sensible.
  switch (kind) {
    case "chat":   return "famchat";
    case "status": return "famstatus";
    case "mood":   return "famstatus"; // FamMood folded into FamStatus
    case "list":   return "famlist";
    case "tree":   return "famtree";
    case "match":  return "fammatch";
    case "tag":    return "fammembers";
    case "system": return "today";
  }
}

// `just now` < 60s, `N 分鐘前` < 1h, `N 小時前` < 24h, else
// `M月D日`. Same format as TodayView's `formatRelative`; we
// duplicate rather than share because that helper is private
// to TodayView.
function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "剛剛";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分鐘前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小時前`;
  return new Intl.DateTimeFormat("zh-HK", { month: "numeric", day: "numeric" })
    .format(new Date(iso));
}

function rowToNotification(row: NotificationRow): Notification {
  return {
    id: row.id,
    kind: row.kind,
    title: row.title,
    body: row.body,
    unread: row.read_at == null,
    time: formatRelative(row.created_at),
    target: targetFromDeepLink(row.deep_link_module, row.kind),
  };
}

// All three realtime payloads share the same `eventType` discriminator
// and `schema` / `table` fields. We type-narrow with a guard so the
// call sites can use `payload.new` / `payload.old` without inline casts.
type AnyChangePayload =
  | RealtimePostgresInsertPayload<NotificationRow>
  | RealtimePostgresUpdatePayload<NotificationRow>
  | RealtimePostgresDeletePayload<NotificationRow>
function isChangePayload(p: unknown): p is AnyChangePayload {
  if (typeof p !== "object" || p === null) return false;
  if (!("eventType" in p)) return false;
  const et = p.eventType;
  return et === "INSERT" || et === "UPDATE" || et === "DELETE";
}
/* ─────────────────────  Hook  ───────────────────── */

const PAGE_SIZE = 50;

export function useNotifications(userId: string | null) {
  const [items, setItems]   = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(userId) && isAuthConfigured());
  const [error, setError]   = useState<string | null>(null);

  // ── Initial fetch + realtime subscription.
  // The realtime channel is scoped to `user_id=eq.${userId}` so the
  // RLS policy (notifications_self, migration 014) and the channel
  // filter agree on what this session can see.
  useEffect(() => {
    if (!isAuthConfigured() || !userId) {
      setItems([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id, kind, title, body, payload, deep_link_module, read_at, created_at, user_id")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(PAGE_SIZE);
      if (cancelled) return;
      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }
      setItems((data ?? []).map(rowToNotification));
      setLoading(false);
    })();

    const ch = supabase
      .channel(`rt:notifications:user=${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (raw: unknown) => {
          if (!isChangePayload(raw) || raw.eventType !== "INSERT") return;
          const row = raw.new;
          setItems((prev) => {
            if (prev.some((n) => n.id === row.id)) return prev;
            return [rowToNotification(row), ...prev].slice(0, PAGE_SIZE);
          });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (raw: unknown) => {
          if (!isChangePayload(raw) || raw.eventType !== "UPDATE") return;
          const row = raw.new;
          setItems((prev) => prev.map((n) => (n.id === row.id ? rowToNotification(row) : n)));
        },
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "notifications",
        filter: `user_id=eq.${userId}`,
        },
        (raw: unknown) => {
          if (!isChangePayload(raw) || raw.eventType !== "DELETE") return;
          // Supabase sends the deleted row in `old` (Partial<T>).
          const old = raw.old as Partial<NotificationRow>;
          if (typeof old.id !== "string") return;
          setItems((prev) => prev.filter((n) => n.id !== old.id));
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(ch);
    };
  }, [userId]);

  // ── markRead: optimistic + write. On failure, refetch.
  const markRead = useCallback(async (id: string) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false } : n)));
    if (!userId) return;
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id)
      .eq("user_id", userId);
    if (error) {
      setError(error.message);
      // Rollback via refetch so local state matches the server.
      const { data } = await supabase
        .from("notifications")
        .select("id, kind, title, body, payload, deep_link_module, read_at, created_at, user_id")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(PAGE_SIZE);
      if (data) setItems(data.map(rowToNotification));
    }
  }, [userId]);

  // ── markAllRead: optimistic + write. We PATCH `user_id=... and
  // read_at is null` so any unread rows the user has — including
  // ones we haven't fetched yet (over the PAGE_SIZE cap) — get
  // marked. The realtime UPDATE handler will sync them.
  const markAllRead = useCallback(async () => {
    if (!userId) return;
    // Optimistic local update first so the UI flips immediately.
    setItems((prev) => prev.map((n) => (n.unread ? { ...n, unread: false } : n)));
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", userId)
      .is("read_at", null);
    if (error) {
      setError(error.message);
      const { data } = await supabase
        .from("notifications")
        .select("id, kind, title, body, payload, deep_link_module, read_at, created_at, user_id")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(PAGE_SIZE);
      if (data) setItems(data.map(rowToNotification));
    }
  }, [userId]);

  const unreadCount = items.reduce((n, i) => n + (i.unread ? 1 : 0), 0);

  return { items, unreadCount, markAllRead, markRead, loading, error };
}
